import { useEffect, useMemo, useRef, useState } from "react";
import { Box, IconButton, Typography } from "@mui/material";
import {
  GridColDef,
  GridRenderCellParams,
  GridRowSelectionModel,
} from "@mui/x-data-grid";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import FilterAltOffOutlinedIcon from "@mui/icons-material/FilterAltOffOutlined";
import { ClearAllOutlined } from "@mui/icons-material";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import { useDispatch, useSelector } from "react-redux";
import TableHeaderSelectField from "../../../../../components/table-header-select-field/table-header-select-field";
import TableHeaderTextField from "../../../../../components/table-header-text-filed/table-header-text-filed";
import { getInvoicesRequest } from "../../../../../redux/orders/slice";
import { splitCamelCaseToWords } from "../../../../../utils/Formatter";
import { downloadInvoiceRequest } from "../../../../../utils/APICalls";
import InvoiceLineItemsDialog from "../../../invoices/invoice-line-items-dialog";
import Table from "../../../../../components/table/table";
import ErrorNotification from "../../../../../components/error-notification/error-notification";
import Loading from "../../../../../components/loading/loading";
import dayjs from "dayjs";
import AdvancedDatePicker from "../../../../../components/advanced-date-picker/advanced-date-picker";

interface ClientInvoicesProps {
  clientDetails?: any;
}

export default function ClientInvoices({ clientDetails }: ClientInvoicesProps) {
  const dispatch = useDispatch();
  const [rowData, setRowData] = useState<any>([]);
  const [showTableFilter, setShowTableFilter] = useState(true);
  const [headerHeight, setHeaderHeight] = useState(104);
  const [paginationModel, setPaginationModel] = useState<any>();
  const [filterParams, setFilterParams] = useState({
    isClosed: true,
    page: 1,
    pageSize: 10,
    clientId: clientDetails?.id,
    subDivisionName: "",
    invoiceType: "",
    invoiceNumber: "",
    projectName: "",
    totalAmount: "",
    closedDate: "",
    sortColumn: "",
    sortOrder: "",
  });
  const [rowSelectionModel, setRowSelectionModel] =
    useState<GridRowSelectionModel>([]);
  const [showInvoiceDialog, setShowInvoiceDialog] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState<any>(null);
  const [filterResetKey, setFilterResetKey] = useState(0);
  const [isDownloading, setIsDownloading] = useState(false);
  const [showLoading, setShowLoading] = useState(false);
  const [showErrorNotification, setShowErrorNotification] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const { loading, invoices } = useSelector((state: any) => state.orders);

  const rowCountRef = useRef(invoices?.totalCount || 0);
  const rowCount = useMemo(() => {
    if (invoices?.totalCount !== undefined) {
      rowCountRef.current = invoices?.totalCount;
    }
    return rowCountRef.current;
  }, [invoices?.totalCount]);

  const column: GridColDef[] = [
    {
      field: "invoiceType",
      headerName: "Invoice Frequency",
      flex: 1,
      sortable: false,
      resizable: false,
      headerClassName: "no-separator",
      renderCell: (params: GridRenderCellParams<any>) => (
        <Typography variant="body2" sx={{ fontWeight: "medium", pt: 2 }}>
          {splitCamelCaseToWords(params.row.invoiceType) || "-"}
        </Typography>
      ),
      renderHeader: (params) => (
        <TableHeaderSelectField
          key={`invoiceType`}
          field="invoiceType"
          sortField="invoiceType"
          headerName={params.colDef.headerName}
          width={params.colDef.computedWidth}
          showFilter={showTableFilter}
          onFilterSubmit={onFilterUsers}
          values={["Individual", "MonthlyBatch", "Project", "SemiMonthlyBatch"]}
        />
      ),
    },
    {
      field: "invoiceNumber",
      headerName: "Invoice #",
      flex: 1,
      sortable: false,
      resizable: false,
      headerClassName: "no-separator",
      renderCell: (params: GridRenderCellParams<any>) => (
        <Typography variant="body2" sx={{ fontWeight: "medium", pt: 2 }}>
          {params.row.invoiceNumber || "000010-B"}
        </Typography>
      ),
      renderHeader: (params) => (
        <TableHeaderTextField
          key={`invoiceNumber`}
          field="invoiceNumber"
          sortField="invoicenumber"
          headerName={params.colDef.headerName}
          width={params.colDef.computedWidth}
          showFilter={showTableFilter}
          onFilterSubmit={onFilterUsers}
        />
      ),
    },
    {
      field: "projectName",
      headerName: "Project Name",
      flex: 1,
      sortable: false,
      resizable: false,
      headerClassName: "no-separator",
      renderCell: (params: GridRenderCellParams<any>) => (
        <Typography variant="body2" sx={{ pt: 2 }}>
          {params.row.projectName || ""}
        </Typography>
      ),
      renderHeader: (params) => (
        <TableHeaderTextField
          key={`projectName`}
          field="projectName"
          sortField="projectname"
          headerName={params.colDef.headerName}
          width={params.colDef.computedWidth}
          showFilter={showTableFilter}
          onFilterSubmit={onFilterUsers}
        />
      ),
    },
    {
      field: "totalAmount",
      headerName: "Total Invoice Amount",
      flex: 1,
      sortable: false,
      renderCell: (params: GridRenderCellParams<any>) => (
        <Typography variant="body2" sx={{ fontWeight: "bold", pt: 2 }}>
          ${parseFloat(params.row.totalAmount as any)?.toFixed(2) || "0.00"}
        </Typography>
      ),
      renderHeader: (params) => (
        <TableHeaderTextField
          key={`totalAmount`}
          field="totalAmount"
          sortField="totalAmount"
          headerName={params.colDef.headerName}
          width={params.colDef.computedWidth}
          showFilter={showTableFilter}
          onFilterSubmit={onFilterUsers}
        />
      ),
    },
    {
      field: "closedAt",
      headerName: "Invoiced Date",
      flex: 1,
      sortable: false,
      valueFormatter: (value?: string) =>
        value ? dayjs(value).format("MM/DD/YYYY") : " ",
      renderHeader: (params) => (
        <AdvancedDatePicker
          key={`closedAt`}
          field="closedDate"
          sortField="closedAt"
          headerName={params.colDef.headerName}
          width={params.colDef.computedWidth}
          showFilter={showTableFilter}
          onFilterSubmit={onFilterUsers}
        />
      ),
    },
  ];

  useEffect(() => {
    setPaginationModel({
      page: 0,
      pageSize: 10,
    });
    setFilterParams({
      isClosed: true,
      page: 1,
      pageSize: 10,
      clientId: "",
      subDivisionName: "",
      invoiceType: "",
      invoiceNumber: "",
      projectName: "",
      totalAmount: "",
      closedDate: "",
      sortColumn: "",
      sortOrder: "",
    });
  }, [clientDetails]); // eslint-disable-line

  useEffect(() => {
    const dataWithIds = (invoices?.items || []).map(
      (item: any, index: number) => ({
        ...item,
        id: item.id,
      })
    );
    setRowData(dataWithIds);
  }, [invoices]);

  useEffect(() => {
    const filters: any = { ...filterParams };
    const textFilterFields = [
      "clientName",
      "subDivisionName",
      "invoiceType",
      "invoiceNumber",
      "projectName",
      "totalAmount",
      "closedDate",
    ];
    textFilterFields.forEach((field) => {
      if (filters.hasOwnProperty(field) && filters[field] === "") {
        delete filters[field];
      }
    });

    if (filters.sortColumn === "" || filters.sortOrder === "") {
      delete filters.sortColumn;
      delete filters.sortOrder;
    }

    dispatch(getInvoicesRequest(filters));
    /* eslint-disable */
  }, [filterParams]); /* eslint-enable */

  const onFilterClick = (value: boolean) => {
    setShowTableFilter(value);
    setHeaderHeight(value ? 104 : 56);
  };

  const handleRowClick = (params: any) => {
    setSelectedInvoice(params.row);
    setShowInvoiceDialog(true);
  };

  const handleCloseDialog = () => {
    setShowInvoiceDialog(false);
    setSelectedInvoice(null);
  };

  const handleDownloadInvoice = async () => {
    const invoiceIds = rowSelectionModel
      .filter((id) => id != null)
      .map((id) => String(id));
    if (!invoiceIds.length) return;

    setIsDownloading(true);
    setShowLoading(true);
    try {
      const response = await downloadInvoiceRequest(invoiceIds);

      if (response && response.status === 200) {
        const mimeType =
          response.headers["content-type"] || "application/pdf";
        const blob = new Blob([response.data], { type: mimeType });

        const contentDisposition = response.headers["content-disposition"];
        let fileName =
          invoiceIds.length === 1
            ? `Invoice-${rowData.find((row: any) => String(row.id) === invoiceIds[0])
              ?.invoiceNumber || invoiceIds[0]
            }`
            : "Invoices";
        if (contentDisposition) {
          const fileNameMatch = contentDisposition.match(/filename="(.+)"/);
          if (fileNameMatch && fileNameMatch.length === 2) {
            fileName = fileNameMatch[1];
          }
        }

        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.setAttribute("download", fileName);
        a.target = "_blank";
        a.rel = "noopener noreferrer";
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      } else {
        setErrorMessage("Failed to download invoice. Please try again.");
        setShowErrorNotification(true);
      }
    } catch (error) {
      setErrorMessage("An unexpected error occurred. Please try again.");
      setShowErrorNotification(true);
    } finally {
      setIsDownloading(false);
      setShowLoading(false);
    }
  };

  const onFilterUsers = (data: any) => {
    const keys = Object.keys(data);
    if (keys.includes("sortColumn")) {
      setFilterParams({ ...filterParams, ...data });
    } else if (keys.includes("closedDate.Operator")) {
      setFilterParams({ ...filterParams, ...data });
    } else {
      keys.forEach((key: string) => {
        if (filterParams.hasOwnProperty(key) && data[key] === "") {
          const { [key]: removedKey, ...filters } = filterParams as any;
          setFilterParams(filters);
        } else if (data[key] || data[key] === 0 || data[key] === false) {
          setFilterParams({ ...filterParams, [key]: data[key] });
        }
      });
    }
  };

  const onClearAllFilters = () => {
    setPaginationModel({
      page: 0,
      pageSize: 10,
    });
    setFilterParams({
      isClosed: true,
      page: 1,
      pageSize: 10,
      clientId: clientDetails?.id,
      subDivisionName: "",
      invoiceType: "",
      invoiceNumber: "",
      projectName: "",
      totalAmount: "",
      closedDate: "",
      sortColumn: "",
      sortOrder: "",
    });
    setFilterResetKey((prev) => prev + 1);
  };

  return (
    <>
      <Box
        className="clients-header"
        sx={{
          mt: 3,
          mb: 1.5,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box display={"flex"} alignItems={"center"}>
          <IconButton
            aria-label="download-invoice"
            disabled={rowSelectionModel.length === 0 || isDownloading}
            onClick={handleDownloadInvoice}
            sx={{
              color:
                rowSelectionModel.length === 0
                  ? "text.disabled"
                  : "primary.main",
            }}
          >
            <DownloadOutlinedIcon sx={{ fontSize: 24 }} />
          </IconButton>
        </Box>
        <Box>
          <IconButton aria-label="clear-all" onClick={onClearAllFilters}>
            <ClearAllOutlined color="primary" />
          </IconButton>
          <IconButton
            aria-label="filter"
            onClick={() => onFilterClick(!showTableFilter)}
          >
            {showTableFilter ? (
              <FilterAltOffOutlinedIcon color="primary" />
            ) : (
              <FilterAltOutlinedIcon color="primary" />
            )}
          </IconButton>
        </Box>
      </Box>

      <Box sx={{ mx: 2 }}>
        <Table
          key={filterResetKey}
          rowData={rowData}
          rowCount={rowCount}
          loading={loading}
          columnDef={column}
          headerHeight={headerHeight}
          disableColumnMenu={true}
          disableColumnResize={false}
          paginationModel={paginationModel}
          checkboxSelection={true}
          onPaginationModelChange={(newPaginationModel: any) => {
            setPaginationModel(newPaginationModel);
            setFilterParams((prev) => ({
              ...prev,
              page: newPaginationModel.page + 1,
              pageSize: newPaginationModel.pageSize,
            }));
          }}
          onRowSelectionModelChange={(newRowSelectionModel: any) => {
            setRowSelectionModel(newRowSelectionModel);
          }}
          rowSelectionModel={rowSelectionModel}
          onRowClick={handleRowClick}
        />
      </Box>

      <InvoiceLineItemsDialog
        key={
          selectedInvoice?.id ||
          selectedInvoice?.invoiceId ||
          selectedInvoice?.invoiceNumber ||
          "empty"
        }
        showDialog={showInvoiceDialog}
        closeDialog={handleCloseDialog}
        invoiceDetails={selectedInvoice}
        isClosed={true}
        invoiceId={selectedInvoice?.id || selectedInvoice?.invoiceId || ""}
      />

      {showLoading && <Loading />}
      <ErrorNotification
        show={showErrorNotification}
        message={errorMessage}
        onClose={() => setShowErrorNotification(false)}
      />
    </>
  );
}
